import React from 'react'
import { Modal, ModalProps } from '../modal'
import { Nilable } from 'tsdef'
import { ID, useHasSeenPricingTransparencyModal } from '~/service'

import styles from './PricingTransparencyModal.module.scss'

export interface PricingTransparencyModalProps
  extends Omit<ModalProps, 'children' | 'isOpen' | 'setIsOpen'> {
  /**
   * The text to display in the modal. If nil, the modal will never be shown.
   */
  text: Nilable<string>
  /**
   * The id of the current community so we can ensure the modal is shown once
   * per community.
   */
  communityId: Nilable<ID>
  /**
   * Disable saving of the "acknowledged" state to local storage. This is useful
   * for our Storybook.
   */
  disableStorage?: boolean
}

/**
 * `<PricingTransparencyModal>` displays a modal with a describing pricing
 * definitions and what price values in the app represent. The modal will be
 * shown immediately when mounted, and can be dismissed by the user. It will
 * only be shown once (using local storage to track if it has been shown before).
 */
export const PricingTransparencyModal = React.memo(
  ({
    communityId,
    text,
    disableStorage,
    ...rest
  }: PricingTransparencyModalProps) => {
    const [hasSeenModal, markAsSeen] = useHasSeenPricingTransparencyModal(
      communityId,
      disableStorage
    )

    return (
      <Modal
        label="Pricing Transparency Information"
        data-testid="PricingTransparencyModal"
        layout="scrollable"
        {...rest}
        isOpen={!!text && !hasSeenModal}
        setIsOpen={markAsSeen}
        onRequestClose={markAsSeen}
        closeOnBackgroundClick={true}
      >
        <div
          className={styles.content}
          dangerouslySetInnerHTML={React.useMemo(
            () => ({ __html: text! }),
            [text]
          )}
        />
      </Modal>
    )
  }
)
